import { jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useState, useRef, useCallback, Suspense, useEffect } from "react";
import { useThemeColors, useThemeFonts, useEditMode, useWidgetThemeParams, useWidgetTheme, updateWidgetConfig, WidgetWrapper } from "@brandclub/bc-widgets";
import { NativeAdapter } from "@brandclub/concierge-widgets";
import { useBrandAppConfig, useDomainConfig } from "../../../api/cache/appConfigQueryOptions.js";
import { getAppConsistentParams } from "../../../utils/appUtils.js";
import { useConciergeContext } from "../../ConciergeContextProvider.js";
import { useProductClickTracking } from "../../hooks/useProductClickTracking.js";
import { getHeadersWithUniqueVisitorId } from "../../../Auth/index.js";
const MfWidgetRegistryContext = createContext({});
function useAuthToken() {
  const [token, setToken] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getHeadersWithUniqueVisitorId().then((headers) => {
      if (cancelled) return;
      const authHeader = (headers == null ? void 0 : headers.Authorization) || (headers == null ? void 0 : headers.authorization);
      if (typeof authHeader === "string" && authHeader.startsWith("Bearer ")) {
        setToken(authHeader.slice(7));
      }
    }).catch(() => {
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return { token, status: token ? "authenticated" : "unknown" };
}
function MfWidgetLoader({
  widgetId,
  toolInput = {},
  toolOutput = null,
  dataParts = [],
  partState = "complete",
  onProductClick
}) {
  const { trackProductClick } = useProductClickTracking();
  const { data: brandAppConfig } = useBrandAppConfig();
  const { data: domainConfig } = useDomainConfig();
  const { apiBaseUrl, retailerIdToNameMap } = useConciergeContext();
  const auth = useAuthToken();
  const { themeColors } = useThemeColors();
  const { themeFonts } = useThemeFonts();
  const isEditMode = useEditMode();
  const themeParams = useWidgetThemeParams();
  const { data: widgetThemeData } = useWidgetTheme();
  const widgetRegistry = useContext(MfWidgetRegistryContext);
  const WidgetComponent = widgetRegistry[widgetId];
  if (!WidgetComponent) return null;
  const urlSearchParams = getAppConsistentParams(window.location.search);
  const themeConfig = useMemo(() => {
    var _a, _b, _c, _d;
    const fonts = ((_a = widgetThemeData == null ? void 0 : widgetThemeData.config) == null ? void 0 : _a.fonts) ?? themeFonts ?? {};
    let resolvedFont;
    for (const fontValue of Object.values(fonts)) {
      if (fontValue == null ? void 0 : fontValue.name) {
        resolvedFont = `"${fontValue.name}", system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
        break;
      }
    }
    return {
      colors: ((_b = widgetThemeData == null ? void 0 : widgetThemeData.config) == null ? void 0 : _b.colors) ?? themeColors ?? {},
      fonts,
      icons: ((_c = widgetThemeData == null ? void 0 : widgetThemeData.config) == null ? void 0 : _c.icons) ?? {},
      widgetConfig: ((_d = widgetThemeData == null ? void 0 : widgetThemeData.config) == null ? void 0 : _d.widgetConfig) ?? null,
      resolvedFont
    };
  }, [widgetThemeData, themeColors, themeFonts]);
  const [editReg, setEditReg] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const adapterRef = useRef(null);
  const handleRegisterEditFields = useCallback((regWidgetId, fields, defaultState, currentState) => {
    regWidgetId.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    setEditReg({
      widgetId: regWidgetId,
      fields,
      defaultState,
      currentState
    });
    setEditValues(currentState);
  }, []);
  if (!adapterRef.current) {
    adapterRef.current = new NativeAdapter(handleRegisterEditFields);
  }
  const handleEditChange = useCallback((key, value) => {
    setEditValues((prev) => {
      var _a;
      const next = { ...prev, [key]: value };
      (_a = adapterRef.current) == null ? void 0 : _a.pushEditChange(key, value, next);
      return next;
    });
  }, []);
  const handleSave = useCallback(async () => {
    var _a;
    if (!themeParams.themeId || !themeParams.editThemeToken || !editReg) return;
    setIsSaving(true);
    try {
      await updateWidgetConfig({
        themeId: themeParams.themeId,
        widgetId: editReg.widgetId,
        widgetState: editValues,
        editThemeToken: themeParams.editThemeToken
      });
      (_a = adapterRef.current) == null ? void 0 : _a.pushEditSaved(editValues);
    } catch (error) {
      console.error("[MfWidgetLoader] Failed to save widget config:", error);
    } finally {
      setIsSaving(false);
    }
  }, [themeParams, editReg, editValues]);
  const data = useMemo(() => {
    var _a;
    return {
      auth,
      baseUrl: apiBaseUrl || "/api-rewards",
      ctx: {
        brandAppConfig: brandAppConfig || null,
        domainConfig: domainConfig || null,
        retailerIdToNameMap: retailerIdToNameMap || {}
      },
      urlSearchParams,
      theme: {
        colors: {
          ...themeColors,
          textPrimary: themeColors.foreground || themeColors.textPrimary,
          textSecondary: themeColors.muted || themeColors.mutedForeground || themeColors.textSecondary,
          tertiary: themeColors.border || themeColors.tertiary
        },
        fonts: themeFonts,
        resolvedFont: ((_a = themeFonts.body) == null ? void 0 : _a.name) || ""
      },
      themeConfig,
      editMode: isEditMode,
      toolInput,
      toolOutput,
      dataParts,
      partState,
      savedState: null,
      onAction: (action, params) => {
        if (action === "product_click" && (params == null ? void 0 : params.stacklineSku)) {
          trackProductClick({
            stacklineSku: params.stacklineSku,
            productTitle: params.productTitle || "",
            clickSource: params.clickSource || "widget",
            retailerId: params.retailerId,
            retailPrice: params.retailPrice,
            hasReward: params.hasReward,
            rewardAmount: params.rewardAmount
          });
        }
        if (action === "navigate_to_product" && (params == null ? void 0 : params.stacklineSku)) {
          onProductClick == null ? void 0 : onProductClick(params.stacklineSku);
        }
      },
      onCommand: () => {
      },
      onSaveState: () => {
      }
    };
  }, [auth, apiBaseUrl, brandAppConfig, domainConfig, retailerIdToNameMap, urlSearchParams, themeColors, themeFonts, themeConfig, isEditMode, toolInput, toolOutput, dataParts, partState, trackProductClick, onProductClick]);
  const widgetContent = /* @__PURE__ */ jsx(Suspense, { fallback: /* @__PURE__ */ jsx("div", { style: { minHeight: 40 } }), children: /* @__PURE__ */ jsx(WidgetComponent, { data, adapter: adapterRef.current }) });
  if (isEditMode && editReg) {
    return /* @__PURE__ */ jsx(
      WidgetWrapper,
      {
        widgetId: editReg.widgetId,
        displayName: editReg.widgetId.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        editFields: editReg.fields,
        editValues,
        onEditChange: handleEditChange,
        onSave: handleSave,
        isSaving,
        themeColors: themeConfig.colors,
        themeFonts: themeConfig.fonts,
        themeIcons: themeConfig.icons,
        children: widgetContent
      }
    );
  }
  return widgetContent;
}
export {
  MfWidgetLoader,
  MfWidgetRegistryContext
};
//# sourceMappingURL=MfWidgetLoader.js.map
